import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLicenseBanner } from '../context/LicenseBannerContext';
import { ROLES } from '../utils/constants';
import { BellIcon, ChevronDownIcon, Bars3Icon, MagnifyingGlassIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '../hooks/useTranslation';
import PostLoginLanguageSwitcher from './PostLoginLanguageSwitcher';
import { useNotifications, useUnreadCount, useMarkNotificationsAsRead } from '../hooks/useApi';
import { useSocket } from '../context/SocketContext';
import inchaaLogoBlue from '../assets/images/inchaa-logo-blue.svg';
import { translateRole } from '../utils/roleTranslations';

const Header = ({ sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { bannerHeight } = useLicenseBanner();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const profileBtnRef = useRef(null);
  const notificationBtnRef = useRef(null);
  const languageBtnRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const { t, isRTL, language } = useTranslation();

  // Fetch notifications and unread count
  const { data: notificationsData, isLoading: notificationsLoading } = useNotifications();
  const { data: unreadCountData, isLoading: unreadCountLoading } = useUnreadCount();
  const markAsReadMutation = useMarkNotificationsAsRead();
  const { isConnected: socketConnected, connectionError, messages, testConnection } = useSocket();

  const notifications = notificationsData?.result || [];
  const unreadCount = unreadCountData?.result || 0;
  const normalizedUserRole = (user?.role ?? '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '')
    .replace(/[_\-]/g, '');

  const getRoleDisplayName = (role) => {
    if (!role) return '';

    const roleStr = String(role);
    const roleUpper = roleStr.toUpperCase();
    const roleLower = roleStr.toLowerCase().trim().replace(/\s+/g, '');
    const normalized = roleLower.replace(/[_\-]/g, '');

    if (
      normalized === ROLES.SUPERADMIN ||
      normalized === 'superadmin' ||
      normalized === 'admin'
    ) {
      return t('auth.superAdmin');
    }
    if (roleUpper === ROLES.CONTRACTOR || roleLower === 'contractor') {
      return t('auth.contractor');
    }
    if (roleUpper === ROLES.CONSULTANT || roleLower === 'consultant') {
      return t('auth.consultant');
    }
    if (roleUpper === ROLES.ENGINEER || roleLower === 'engineer') {
      return t('auth.engineer');
    }

    // Fallback: try best-effort translation for known role tokens
    const translated = translateRole(roleStr, language);
    return translated || roleStr;
  };

  const contractorRoleDisplay = useMemo(() => {
    const roles = user?.contractorRoles;
    if (!roles || roles.length === 0) return null;

    const normalised = roles
      .map(role => role
        ?.toString()
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/[_\-&]/g, '')
      )
      .filter(Boolean);

    // Use "includes" to support combined strings like "maincontractor&subcontractor"
    const hasMain = normalised.some(role => role.includes('maincontractor') || role === 'main');
    const hasSub = normalised.some(role => role.includes('subcontractor') || role === 'sub');

    if (hasMain && hasSub) {
      return `${t('auth.mainContractorRole')} ${t('auth.and')} ${t('auth.subContractorRole')}`;
    }

    if (hasMain) {
      return t('auth.mainContractorRole');
    }

    if (hasSub) {
      return t('auth.subContractorRole');
    }

    // Fall back to comma-separated original roles if no match
    const translatedRoles = roles.map((r) => translateRole(r, language)).filter(Boolean);
    if (translatedRoles.length === 0) return null;
    const joiner = language === 'ar' ? '، ' : ', ';
    return translatedRoles.join(joiner);
  }, [user?.contractorRoles, language, t]);

  const handleLogout = () => {
    setIsProfileOpen(false);
    logout();
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark notification as read
      if (!notification.isRead) {
        await markAsReadMutation.mutateAsync([notification.notificationId]);
      }
      
      // Navigate to the route
      if (notification.route) {
        navigate(notification.route);
        setIsNotificationOpen(false);
      }
    } catch (error) {
      // Still navigate even if marking as read fails
      if (notification.route) {
        navigate(notification.route);
        setIsNotificationOpen(false);
      }
    }
  };

  // Format notification time as dd/mm/yyyy, h:mm AM/PM
  const formatNotificationTime = (createdAt) => {
    if (!createdAt) return 'N/A';
    
    try {
      let date;
      
      // Parse the date string - handle UTC dates correctly
      if (typeof createdAt === 'string') {
        if (createdAt.includes('T') && !createdAt.includes('Z') && !createdAt.match(/[+-]\d{2}:\d{2}$/)) {
          date = new Date(createdAt + 'Z');
        } else {
          date = new Date(createdAt);
        }
      } else {
        date = new Date(createdAt);
      }
      
      if (isNaN(date.getTime())) return 'N/A';
      
      // Format date as dd/mm/yyyy and time in local timezone
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = language === 'ar' 
        ? (hours >= 12 ? 'م' : 'ص')
        : (hours >= 12 ? 'PM' : 'AM');
      const displayHours = hours % 12 || 12;
      
      return `${day}/${month}/${year}, ${displayHours}:${minutes} ${ampm}`;
    } catch (error) {
      return 'N/A';
    }
  };


  useEffect(() => {
    if (isProfileOpen && profileBtnRef.current) {
      const rect = profileBtnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8, // 8px margin
        left: rect.right - 224, // 224px = dropdown width
      });
    }
  }, [isProfileOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (isProfileOpen && profileBtnRef.current && !profileBtnRef.current.contains(e.target)) {
        setIsProfileOpen(false);
      }
      if (isNotificationOpen && notificationBtnRef.current && !notificationBtnRef.current.contains(e.target)) {
        setIsNotificationOpen(false);
      }
      if (isLanguageOpen && languageBtnRef.current && !languageBtnRef.current.contains(e.target)) {
        setIsLanguageOpen(false);
      }
    }
    if (isProfileOpen || isNotificationOpen || isLanguageOpen) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isProfileOpen, isNotificationOpen, isLanguageOpen]);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky z-[60] w-full" dir={isRTL ? 'rtl' : 'ltr'} style={{ top: `${bannerHeight}px` }}>
      <div className="w-full px-2 sm:px-4 md:px-4 lg:px-8 flex items-center h-16 sm:h-20 min-w-0">
        {/* Mobile menu button - visible on mobile and tablet, hidden on desktop (lg and above) */}
        <button
          type="button"
          onClick={() => setSidebarOpen?.(true)}
          className="lg:hidden mr-2 sm:mr-3 inline-flex items-center justify-center rounded-md p-1.5 sm:p-2 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-200 flex-shrink-0"
        >
          <span className="sr-only">{t('header.openSidebar')}</span>
          <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
        </button>

        {/* Logo - visible on mobile only */}
        <div className="lg:hidden mr-2 sm:mr-3 flex-shrink-0">
          <img src={inchaaLogoBlue} alt="Inchaa Logo" className="h-6 sm:h-8" />
        </div>

        {/* Search bar - visible on desktop */}
        <div className="hidden lg:flex items-center flex-1 min-w-0 mr-2 sm:mr-3 md:mr-0">
          <input 
            type="text" 
            placeholder={t('general.search')}
            autoComplete="new-password"
            className="w-full md:w-64 lg:w-80 xl:w-96 px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200 text-gray-700"
            dir={isRTL ? 'rtl' : 'ltr'}
            style={{ textAlign: isRTL ? 'right' : 'left' }}
          />
        </div>
        
        {/* Spacer to push right elements to the end */}
        <div className="flex-1"></div>
        
        {/* Right side elements - Language Switcher (desktop only), Search (mobile), Notifications, Profile */}
        <div className={`flex items-center min-w-0 flex-shrink-0 ${isRTL ? 'space-x-reverse space-x-1 sm:space-x-2 md:space-x-4' : 'space-x-1 sm:space-x-2 md:space-x-4'}`}>
          {/* Search icon button - visible on mobile only */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center rounded-full p-2 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 flex-shrink-0 bg-gray-100"
          >
            <span className="sr-only">{t('general.search')}</span>
            <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          {/* Language Switcher - Globe Icon (mobile only) */}
          <div className="relative flex-shrink-0 lg:hidden" ref={languageBtnRef}>
            <button 
              onClick={() => setIsLanguageOpen(!isLanguageOpen)}
              className="relative p-2 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-colors"
            >
              <GlobeAltIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
            </button>
            
            {/* Language Dropdown */}
            {isLanguageOpen && (
              <div className={`absolute mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[60] ${
                isRTL 
                  ? 'left-2 sm:left-0' 
                  : 'right-2 sm:right-0'
              }`}>
                <div className="px-3 py-2">
                  <PostLoginLanguageSwitcher className="" />
                </div>
              </div>
            )}
          </div>

          {/* Language Switcher - Direct (desktop only) */}
          <div className="hidden lg:block flex-shrink-0">
            <PostLoginLanguageSwitcher className="" />
          </div>
          {/* Notification Bell */}
          <div className="relative flex-shrink-0" ref={notificationBtnRef}>
            <button 
              onClick={() => setIsNotificationOpen(!isNotificationOpen)}
              className="relative p-2 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-colors md:bg-transparent md:hover:bg-gray-100"
            >
              <BellIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 bg-red-500 text-white text-[10px] sm:text-xs rounded-full min-w-[1rem] sm:min-w-[1.25rem] h-4 sm:h-5 px-1 flex items-center justify-center font-medium">
                  {unreadCount}
                </span>
              )}
              {/* Real-time connection indicator */}
              {/* {socketConnected && (
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
              )} */}
            </button>
            
            {/* Notification Dropdown */}
            {isNotificationOpen && (
              <div className={`absolute mt-2 w-[calc(100vw-5rem)] sm:w-100 max-w-sm bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[60] ${
                isRTL 
                  ? 'left-2 sm:left-0' 
                  : 'right-2 sm:right-0'
              }`}
              >
                <div className="px-3 sm:px-4 py-2 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900">{t('header.notifications')}</h3>
                    <div className="flex items-center space-x-2">
                      {/* Connection Status */}
                      <div className={`flex items-center space-x-1 ${isRTL ? 'space-x-reverse' : ''}`}>
                        {/* <div className={`w-2 h-2 rounded-full ${socketConnected ? 'bg-green-500' : 'bg-red-500'}`}></div> */}
                        <span className="text-xs text-gray-500">
                          {/* {socketConnected ? 'Live' : 'Offline'} */}
                        </span>
                      </div>
                      {/* Diagnose Button (Development) */}
                  
                    </div>
                  </div>
                  {/* {connectionError && (
                    <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                      {connectionError}
                    </div>
                  )} */}
                  {/* SignalR Messages Debug (Development) */}
                  {/* {process.env.NODE_ENV === 'development' && messages.length > 0 && (
                    <div className="mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      SignalR Messages: {messages.length}
                    </div>
                  )} */}
                </div>
                {notificationsLoading ? (
                  <div className="px-3 sm:px-4 py-6 sm:py-8 text-center">
                    <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="text-xs sm:text-sm text-gray-500 mt-2">{t('header.loadingNotifications')}</p>
                  </div>
                ) : notifications.length > 0 ? (
                  <div className="max-h-48 sm:max-h-64 overflow-y-auto">
                    {notifications.map((notification) => (
                      <div 
                        key={notification.notificationId} 
                        className={`px-3 sm:px-4 py-2 sm:py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors ${
                          !notification.isRead ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start">
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs sm:text-sm font-medium truncate ${!notification.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                              {notification.title}
                            </p>
                            {notification.body && (
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">{notification.body}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              {formatNotificationTime(notification.createdAt)}
                            </p>
                          </div>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-1 flex-shrink-0"></div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 sm:px-4 py-6 sm:py-8 text-center">
                    <p className="text-xs sm:text-sm text-gray-500">{t('header.noNotifications')}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="hidden sm:block w-px h-6 sm:h-8 bg-gray-300 flex-shrink-0"></div>
          
          {/* User Profile */}
          <div className="relative flex-shrink-0" ref={profileBtnRef}>
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className={`flex items-center p-1 sm:p-2 rounded-full md:rounded-lg bg-gray-100 md:bg-transparent hover:bg-gray-200 md:hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-colors ${
                isRTL ? 'space-x-reverse space-x-3 sm:space-x-4 md:space-x-5' : 'space-x-1 sm:space-x-2 md:space-x-3'
              }`}
            >
              {/* User Avatar */}
              {user?.profileImage ? (
                <img 
                  src={user.profileImage} 
                  alt={user?.name || 'User'} 
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm sm:text-lg font-bold">{user?.name?.charAt(0) || 'U'}</span>
                </div>
              )}
              
              {/* User Info - hidden on mobile, visible on desktop */}
              <div
                className={`hidden lg:block min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}
                style={{ marginInlineStart: isRTL ? '10px' : '10px' }}
              >
                <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">{user?.name || t('header.noUser')}</p>
                <p className="mt-0.5 text-xs sm:text-sm text-gray-500 truncate">
                  {(normalizedUserRole === ROLES.CONTRACTOR ? contractorRoleDisplay : null) ||
                    getRoleDisplayName(user?.role) ||
                    t('header.noRole')}
                </p>
              </div>
              
              {/* Dropdown Arrow - hidden on mobile, visible on desktop */}
              <ChevronDownIcon className="hidden md:block h-4 w-4 text-gray-400 flex-shrink-0" />
            </button>
            
            {/* Profile Dropdown */}
            {isProfileOpen && (
              <div className={`absolute mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[60] ${isRTL ? 'left-0' : 'right-0'} max-w-[calc(100vw-1rem)]`}>
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-base font-semibold text-gray-900 break-words">{user?.name || t('header.noUser')}</p>
                  <p className="text-sm text-gray-500 mt-0.5 break-all">{user?.email || t('header.noEmail')}</p>
                  {normalizedUserRole === ROLES.CONTRACTOR && contractorRoleDisplay && (
                    <p className="text-sm text-gray-600 mt-1 break-words">{contractorRoleDisplay}</p>
                  )}
                </div>
                <div className="py-1">
                  <button
                    onClick={handleLogout}
                    className={`w-full ${isRTL ? 'text-right' : 'text-left'} px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>{t('header.signOut')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 